import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { CustomerService } from '../services/customer-service';

export interface Lead {
  id: string;
  address: string;
  postcode: string;
  people: number;
  value: string;
  status: string;
  priority: string;
  phone?: string;
}

export interface Package {
  name: string;
  tier: 'basic' | 'standard' | 'premium' | 'platinum' | 'bonus';
  business: boolean;
  price: string;
  data: number;
  speed: number;
  features: string;
  badge?: string;
}

export interface Customer {
  firstName: string;
  lastName: string;
  address: {
    address: string;
    line1?: string;
    line2?: string;
  };
  postcode: string;
  country: string;
}

export interface Billing {
  name: string;
  address: string;
  sortCode: string;
  iban: string;
  DOB: Date;
}

export interface SalesFunnelData {
  lead: Lead | null;
  package: Package | null;
  customer: Customer | null;
  billing: Billing | null;
  discount: number;
  selectedPackage?: Package;
}

@Injectable({
  providedIn: 'root',
})
export class SalesFunnelDataService {
  private customerService = inject(CustomerService);

  private dataSubject = new BehaviorSubject<SalesFunnelData>({
    lead: null,
    package: null,
    customer: null,
    billing: null,
    discount: 0,
  });

  public data$: Observable<SalesFunnelData> = this.dataSubject.asObservable();

  getData(): SalesFunnelData {
    return this.dataSubject.value;
  }

  fetchCustomerData(): Observable<Customer | null> {
    const current = this.dataSubject.value;
    if (current.customer || !current.lead) {
      return of(null);
    }
    return this.customerService
      .getCustomer(current.lead.id)
      .pipe(tap((customer) => this.updateCustomer(customer)));
  }

  updateLead(lead: Lead): void {
    const current = this.dataSubject.value;
    this.dataSubject.next({ ...current, lead });
  }

  updatePackage(pkg: Package): void {
    const current = this.dataSubject.value;
    this.dataSubject.next({ ...current, package: pkg, selectedPackage: pkg });
  }

  updateCustomer(customer: Customer): void {
    const current = this.dataSubject.value;
    this.dataSubject.next({ ...current, customer });
  }

  updateBilling(billing: Billing): void {
    const current = this.dataSubject.value;
    this.dataSubject.next({ ...current, billing });
  }

  updateDiscount(discount: number): void {
    const current = this.dataSubject.value;
    this.dataSubject.next({ ...current, discount });
  }

  reset(): void {
    this.dataSubject.next({
      lead: null,
      package: null,
      customer: null,
      billing: null,
      discount: 0,
    });
  }
}
